import React, { useRef, useState } from 'react';
import { Download, Eye, EyeOff, Plus, Trash2, Upload } from 'lucide-react';
import { portalAdminService } from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

interface Props {
  programId: string;
  sessions: any[];
  onReload: () => Promise<void>;
  onError: (err: any, fallback: string) => void;
  onFlash: (message: string) => void;
}

/** جلسات دوره و جزوه‌های هرکدام */
export const PortalSessionsTab: React.FC<Props> = ({
  programId,
  sessions,
  onReload,
  onError,
  onFlash,
}) => {
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState('');
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      onError(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const uploadMaterial = (sessionId: string, file: File) =>
    run(async () => {
      const form = new FormData();
      form.append('file', file);
      form.append('title', file.name);
      await portalAdminService.addMaterial(sessionId, form);
      await onReload();
      onFlash('فایل اضافه شد.');
    }, 'آپلود فایل انجام نشد.');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-600">{fa(sessions.length)} جلسه</p>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            run(async () => {
              await portalAdminService.createSession(programId, {});
              await onReload();
            }, 'ساخت جلسه انجام نشد.')
          }
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
        >
          <Plus className="h-4 w-4" />
          جلسهٔ جدید
        </button>
      </div>

      {sessions.map((session) => {
        const open = openId === session.id;
        return (
          <div key={session.id} className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center gap-2 p-3">
              <button
                type="button"
                onClick={() => setOpenId(open ? '' : session.id)}
                className="flex min-w-0 flex-1 items-center gap-2 text-right"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-xs font-black text-blue-700">
                  {fa(session.number)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-800">
                  {session.title}
                </span>
              </button>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-600">
                {fa(session.materials?.length ?? 0)} فایل · {fa(session._count?.tasks ?? 0)} تسک
              </span>
              <button
                type="button"
                onClick={() =>
                  run(async () => {
                    await portalAdminService.updateSession(session.id, {
                      isPublished: !session.isPublished,
                    });
                    await onReload();
                  }, 'تغییر وضعیت انجام نشد.')
                }
                className={
                  'flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold ' +
                  (session.isPublished ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500')
                }
              >
                {session.isPublished ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                {session.isPublished ? 'منتشر' : 'پیش‌نویس'}
              </button>
            </div>

            {open ? (
              <div className="space-y-3 border-t border-gray-100 p-3">
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-bold text-gray-600">عنوان</span>
                    <input
                      defaultValue={session.title}
                      onBlur={(e) =>
                        e.target.value !== session.title &&
                        run(async () => {
                          await portalAdminService.updateSession(session.id, {
                            title: e.target.value,
                          });
                          await onReload();
                        }, 'ذخیره انجام نشد.')
                      }
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">
                      تاریخ برگزاری
                    </span>
                    <input
                      defaultValue={session.heldOn ?? ''}
                      placeholder="۱۴۰۵/۰۶/۱۵"
                      onBlur={(e) =>
                        run(async () => {
                          await portalAdminService.updateSession(session.id, {
                            heldOn: e.target.value,
                          });
                          await onReload();
                        }, 'ذخیره انجام نشد.')
                      }
                      className={inputClass}
                    />
                  </label>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">نوع برگزاری</span>
                    <select
                      defaultValue={session.mode}
                      onChange={(e) =>
                        run(async () => {
                          await portalAdminService.updateSession(session.id, {
                            mode: e.target.value,
                          });
                          await onReload();
                        }, 'ذخیره انجام نشد.')
                      }
                      className={inputClass}
                    >
                      <option value="IN_PERSON">حضوری</option>
                      <option value="ONLINE">آنلاین</option>
                      <option value="HYBRID">ترکیبی</option>
                    </select>
                  </label>
                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-bold text-gray-600">
                      لینک جلسهٔ آنلاین
                    </span>
                    <input
                      defaultValue={session.meetingUrl ?? ''}
                      dir="ltr"
                      onBlur={(e) =>
                        run(async () => {
                          await portalAdminService.updateSession(session.id, {
                            meetingUrl: e.target.value,
                          });
                          await onReload();
                        }, 'ذخیره انجام نشد.')
                      }
                      className={inputClass + ' text-left'}
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-gray-600">توضیحات</span>
                  <textarea
                    rows={2}
                    defaultValue={session.description ?? ''}
                    onBlur={(e) =>
                      run(async () => {
                        await portalAdminService.updateSession(session.id, {
                          description: e.target.value,
                        });
                        await onReload();
                      }, 'ذخیره انجام نشد.')
                    }
                    className={inputClass}
                  />
                </label>

                <div className="rounded-lg border border-gray-100 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-700">جزوه و فایل‌ها</p>
                    <input
                      ref={(node) => {
                        fileRefs.current[session.id] = node;
                      }}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadMaterial(session.id, file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => fileRefs.current[session.id]?.click()}
                      className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[11px] font-bold text-blue-700"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      آپلود فایل
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    {(session.materials ?? []).map((material: any) => (
                      <div
                        key={material.id}
                        className="flex items-center gap-2 rounded-lg border border-gray-100 px-2.5 py-2"
                      >
                        <Download className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="min-w-0 flex-1 truncate text-xs font-bold text-gray-700">
                          {material.title}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            run(async () => {
                              if (!window.confirm('این فایل حذف شود؟')) return;
                              await portalAdminService.deleteMaterial(material.id);
                              await onReload();
                            }, 'حذف انجام نشد.')
                          }
                          className="shrink-0 rounded p-1 text-red-500 hover:bg-red-50"
                          aria-label="حذف"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {!(session.materials ?? []).length ? (
                      <p className="py-2 text-center text-[11px] font-bold text-gray-400">
                        هنوز فایلی اضافه نشده.
                      </p>
                    ) : null}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    run(async () => {
                      if (!window.confirm('این جلسه حذف شود؟')) return;
                      await portalAdminService.deleteSession(session.id);
                      await onReload();
                    }, 'حذف جلسه انجام نشد.')
                  }
                  className="flex items-center gap-1.5 text-[11px] font-bold text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  حذف جلسه
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
